/**
 * The leaderboard wire shapes + formatting, shared by server and client.
 *
 * Born as the online-time board (Lightweight Accounts Slice D, ADR-0043): a
 * ranking of reserved names by total cumulative connected time (`totalOnlineMs`,
 * Slice C / ADR-0040), read from the server's in-memory account map
 * (authoritative, current to the last checkpoint) and exposed two ways - a
 * `GET /leaderboard` HTTP route the splash fetches before the WS connects, and an
 * in-world `/top` chat command. The Rat Swarm PRD (#222, ADR-0115) generalized it:
 * the same route now also ranks by the durable **Gold** balance (`?board=gold`),
 * and later slices added the **Duels** win/loss board (`?board=duels`, ADR-0147)
 * and the **Storm Marks** board (`?board=storm`, ADR-0149), so this tiny module
 * carries every board's entry shapes and formatters. Which *accounts* may appear is
 * decided server-side at one shared seam (ADR-0386): an account with a live ban or an
 * admin leaderboard-hide is absent from every board's rows, `total` and rank - a
 * display filter only, so it returns at its earned rank when unbanned/un-hidden. It is
 * **shared by both the server and the browser** to keep the rank shapes and the
 * human-readable formats identical across every surface (no drift between the
 * JSON the client renders and the line `/top` speaks).
 *
 * Deliberately dependency-free and clock-free: ranking and formatting are pure
 * functions of already-accrued amounts, so this file imports nothing and is
 * trivial to unit-test on either side of the wire.
 */

/**
 * One ranked row as it travels on the wire (the `GET /leaderboard` JSON) and is
 * handed to `/top`: a reserved name's first-claimed display casing plus its total
 * accrued online time in ms. **Only reserved names appear** - an anonymous or
 * blank-passcode session has no account record, so it can never rank (the core
 * ranking rule, shared by every surface). The raw ms rides the wire so each
 * surface formats it itself (the splash and `/top` both via {@link formatOnlineTime});
 * the rows arrive already sorted (online time desc, `createdAt` asc tiebreak).
 */
export interface LeaderboardEntry {
  /** The reserved name in its first-claimed canonical casing (`AccountRecord.displayName`). */
  name: string;
  /** Total cumulative server-measured online time in ms (`AccountRecord.totalOnlineMs`). */
  totalOnlineMs: number;
}

/**
 * One ranked row of the **Gold** board (Rat Swarm Slice A, PRD #222 / ADR-0115):
 * a reserved name plus its durable gold balance. The same core rule as the
 * online-time board applies - **only reserved names appear**, because only a
 * reserved name has the account record the balance lives on (an anonymous
 * session banks nothing durable by design). The raw amount rides the wire so
 * each surface formats it itself (via {@link formatGold}); rows arrive already
 * sorted (gold desc, `createdAt` asc tiebreak), the mirror of the time board.
 */
export interface GoldLeaderboardEntry {
  /** The reserved name in its first-claimed canonical casing (`AccountRecord.displayName`). */
  name: string;
  /** The durable gold balance (`AccountRecord.gold`; whole gold pieces). */
  gold: number;
}

/**
 * One ranked row of the **Duels** board (Dueling Leaderboard, ADR-0147): a reserved
 * name plus its durable win/loss record. The same core rule as the other boards
 * applies - **only reserved names appear**, because only a reserved name has the
 * account record the counters live on (an anonymous duel banks no record for its
 * anonymous side, by design - PRD #252 honor duels). The board is ranked by **total
 * wins descending** (fewer losses first on a tie, then earlier claim); the raw
 * `wins`/`losses` ride the wire so each surface formats the `W-L` string itself (via
 * {@link formatDuelRecord}), the mirror of how the time/gold boards ship raw amounts.
 */
export interface DuelLeaderboardEntry {
  /** The reserved name in its first-claimed canonical casing (`AccountRecord.displayName`). */
  name: string;
  /** Duels won (`AccountRecord.duelWins`; a decided win credits the winner). */
  wins: number;
  /** Duels lost (`AccountRecord.duelLosses`; a decided loss/forfeit credits the loser). */
  losses: number;
}

/** One reserved player's separate Sphere Duel W-L-D record. */
export interface SphereLeaderboardEntry extends DuelLeaderboardEntry {
  draws: number;
  /** Wins divided by all decided matches, in the inclusive range 0 to 1. */
  winRate: number;
}

/**
 * One ranked row of the live **Card Duels** Elo board: a reserved name plus its
 * current integer rating and rated-game count. Every account begins at 1500, but
 * only accounts that have completed a post-transition rated duel appear.
 * rule as the other boards applies - **only reserved names appear**, because only a
 * reserved name owns the account record the counters live on (an anonymous player owns
 * no cards, so a card duel needs both sides reserved - ADR-0286). Only ranked PvP
 * results affect it; Card Master Carl practice is unrated. Rows are ordered by rating,
 * then completed games, then earlier claim.
 */
export interface CardDuelLeaderboardEntry {
  /** The reserved name in its first-claimed canonical casing (`AccountRecord.displayName`). */
  name: string;
  /** Current card-duel Elo rating. */
  rating: number;
  /** Post-transition ranked PvP games completed, including draws. */
  games: number;
}

/** One frozen pre-Elo testing record, retained as a legacy W-L board. */
export interface LegacyCardDuelLeaderboardEntry {
  name: string;
  /** Ranked PvP wins recorded before the Elo cutover. */
  wins: number;
  /** Ranked PvP losses recorded before the Elo cutover. */
  losses: number;
}

/**
 * One ranked row of the **Storm Marks** board (Blackthorn's Storm Slice A, PRD
 * #289 / ADR-0149): a reserved name plus its durable career Storm Marks - one Mark
 * banked per storm survived. The same core rule as the other boards applies -
 * **only reserved names appear**, because only a reserved name has the account
 * record the counter lives on (an anonymous session shows on the live event board
 * in Slice F but banks no durable Mark, by design). The raw count rides the wire so
 * each surface formats it itself (via {@link formatStormMarks}); rows arrive already
 * sorted (marks desc, `createdAt` asc tiebreak), the mirror of the gold board.
 */
export interface StormMarksLeaderboardEntry {
  /** The reserved name in its first-claimed canonical casing (`AccountRecord.displayName`). */
  name: string;
  /** Durable career Storm Marks (`AccountRecord.stormMarks`; +1 per storm survived). */
  stormMarks: number;
}

/**
 * One ranked row of the **Arena** board (Arena Slice A, PRD #334 / ADR-0172): a
 * reserved name plus its durable Arena record - the best single-run score and how
 * many runs it has played. The same core rule as the other boards applies -
 * **only reserved names appear**, because only a reserved name has the account
 * record the counters live on (an anonymous run banks nothing durable, by design -
 * the gold/storm identity model). The board ranks by **best score descending**;
 * both raw counts ride the wire so each surface formats the score itself (via
 * {@link formatArenaScore}) and can show the run count beside it, the mirror of
 * how the duel board ships raw `wins`/`losses`.
 */
export interface ArenaLeaderboardEntry {
  /** The reserved name in its first-claimed canonical casing (`AccountRecord.displayName`). */
  name: string;
  /** The best single Arena run score (`AccountRecord.arenaBestScore`; raised only when beaten). */
  bestScore: number;
  /** How many Arena runs the account has played (`AccountRecord.arenaRuns`; +1 per run, win or die). */
  runs: number;
}

/**
 * Which ranking a leaderboard surface is asking for (Rat Swarm Slice F, PRD
 * #222): `time` is the classic online-time board (the `GET /leaderboard`
 * default), `gold` the durable-gold board (`?board=gold`, ADR-0115), `duels`
 * the win/loss board (`?board=duels`, ADR-0147), `storm` the durable Storm
 * Marks board (`?board=storm`, ADR-0149), and `arena` the durable best-run-score
 * board (`?board=arena`, ADR-0172), and `cards` the live card-duel Elo board
 * (`?board=cards`, ADR-0353). Shared so the window's tab set and
 * `main.ts`'s fetcher can never disagree on the mode strings; the server treats
 * anything it doesn't recognise as the time default, so `time` simply omits the
 * query param.
 */
export type LeaderboardBoard =
  | 'time'
  | 'gold'
  | 'duels'
  | 'sphere'
  | 'storm'
  | 'arena'
  | 'cards';

/**
 * Rows per leaderboard page (Issue #195): the slice one paged `GET /leaderboard`
 * read returns by default and the board window shows per page. Shared here so the
 * server's default and the client's pager can never disagree; ten matches the
 * classic splash top-10, so page 1 is exactly the board everyone already knows.
 */
export const LEADERBOARD_PAGE_SIZE = 10;

/**
 * One page of a ranking as `GET /leaderboard` answers it (Issue #195): the
 * requested slice plus everything a pager needs in the same round trip - the
 * `total` ranked count (for "Page X of Y"), the echoed `offset`, and the asking
 * viewer's own current 1-based `rank` when the request carried `?me=<name>`
 * (`null` when it didn't, or the name doesn't rank), so "Jump to your position"
 * can resolve its target page without ever downloading the full list.
 *
 * Generic over the entry shape (Rat Swarm Slice A, ADR-0115) so the gold board
 * (`?board=gold`, {@link GoldLeaderboardEntry} rows) shares the identical page
 * wrapper; the default keeps every existing online-time consumer source-compatible.
 */
export interface LeaderboardPage<E = LeaderboardEntry> {
  /** The ranked rows of this page (board order: ranked amount desc, older claim first on ties). */
  entries: E[];
  /** How many names rank on the whole board (drives the page count). */
  total: number;
  /** The 0-based rank this page starts at, echoed back from the request. */
  offset: number;
  /** The viewer's own 1-based rank (`?me=`), or `null` when unknown/unranked. */
  rank: number | null;
  /**
   * Which Arena season this page belongs to (arena-seasons, `board=arena` only):
   * the live season's number on a plain read, the archived season's number on a
   * `?season=` read, and `null` when the asked-for archive doesn't exist ("no
   * past seasons yet"). **Absent** on every other board - additive-optional, so
   * older payload shapes and the non-arena boards are untouched.
   */
  season?: number | null;
  /** Which Card Duels view answered (`board=cards` only). */
  cardView?: 'elo' | 'legacy';
}

/**
 * Format an accrued online-time duration (ms) as a compact, human-readable string
 * for display - e.g. `3h 12m`, `12m`, or `0m` for under a minute. Minute
 * granularity is deliberate: a "who stays online longest" leaderboard reads in
 * hours and minutes, not seconds, and the floor keeps a freshly-reserved name
 * (zero time) reading as a tidy `0m` rather than a noisy `0s`. Hours are
 * unbounded (a long-lived banksitter can read `54h 03m`); a negative or
 * non-finite input is clamped to `0m` so a bad value never renders as garbage.
 */
export function formatOnlineTime(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0m';
  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

/**
 * Format a gold balance for display (Rat Swarm Slice A, ADR-0115) - UO-style
 * `1,234 gp`: whole gold pieces (fractions floored - gold is only ever credited
 * in integers, so a fraction is a bad value, not a real balance), grouped with
 * commas so a long-hunted pile stays legible, and a negative or non-finite input
 * clamped to `0 gp` so a bad value never renders as garbage (the
 * {@link formatOnlineTime} discipline). Grouping is hand-rolled rather than
 * `toLocaleString` so the format is byte-identical on every runtime/locale the
 * shared module runs under (server, browser, tests).
 */
export function formatGold(gold: number): string {
  if (!Number.isFinite(gold) || gold <= 0) return '0 gp';
  const digits = String(Math.floor(gold));
  return `${digits.replace(/\B(?=(\d{3})+$)/g, ',')} gp`;
}

/**
 * Format a duel win/loss record for display (Dueling Leaderboard, ADR-0147) as the
 * compact `W-L` string every surface shows - e.g. `12-4`, or `0-0` for a name that
 * has only just started dueling. Both counts are floored to whole non-negative
 * integers (a duel outcome only ever moves a counter by one, so a fraction or a
 * negative is a bad value, not a real tally) and a non-finite input clamps to `0`,
 * so a poisoned counter never renders as garbage - the {@link formatOnlineTime} /
 * {@link formatGold} discipline. Byte-identical on every runtime the shared module
 * runs under (server, browser, tests), so the window, `/topduels`, and any future
 * surface can never drift.
 */
export function formatDuelRecord(wins: number, losses: number): string {
  const w = Number.isFinite(wins) && wins > 0 ? Math.floor(wins) : 0;
  const l = Number.isFinite(losses) && losses > 0 ? Math.floor(losses) : 0;
  return `${w}-${l}`;
}

/** Format a persisted card Elo rating as a whole-number score. */
export function formatElo(rating: number): string {
  return `${Number.isFinite(rating) ? Math.max(0, Math.round(rating)) : 0} Elo`;
}

/**
 * Format a Storm Marks count for display (Blackthorn's Storm Slice A, ADR-0149) as
 * the compact `N Marks` / `1 Mark` string every surface shows - e.g. `12 Marks`,
 * `1 Mark`, or `0 Marks` for a name that has never survived a storm. The count is
 * floored to a whole non-negative integer (a storm only ever credits one Mark, so a
 * fraction is a bad value, not a real tally) and a negative or non-finite input
 * clamps to `0 Marks`, so a poisoned counter never renders as garbage - the
 * {@link formatOnlineTime} / {@link formatGold} / {@link formatDuelRecord}
 * discipline. Byte-identical on every runtime the shared module runs under (server,
 * browser, tests), so the Slice F window tab and any future surface can never drift.
 */
export function formatStormMarks(marks: number): string {
  const n = Number.isFinite(marks) && marks > 0 ? Math.floor(marks) : 0;
  return `${n} ${n === 1 ? 'Mark' : 'Marks'}`;
}

/**
 * Format an Arena best-run score for display (Arena Slice A, PRD #334 / ADR-0172)
 * as the compact `N pts` string every surface shows - e.g. `1,234 pts`, or `0 pts`
 * for a name that died on the first wave. A good run scores in the thousands, so
 * the count gets the gold board's hand-rolled thousands grouping (byte-identical on
 * every runtime/locale the shared module runs under - server, browser, tests). The
 * score is floored to a whole non-negative integer (scoring only ever credits whole
 * points, so a fraction is a bad value, not a real score) and a negative or
 * non-finite input clamps to `0 pts`, so a poisoned counter never renders as
 * garbage - the {@link formatOnlineTime} / {@link formatGold} /
 * {@link formatStormMarks} discipline.
 */
export function formatArenaScore(score: number): string {
  if (!Number.isFinite(score) || score <= 0) return '0 pts';
  const digits = String(Math.floor(score));
  return `${digits.replace(/\B(?=(\d{3})+$)/g, ',')} pts`;
}
